import type { AdminPaymentItem } from '../types/adminPayment';

export interface PaymentKpis {
  total: number;
  succeeded: number;
  pending: number;
  failed: number;
  refunded: number;
  totalAmountMinor: number | null;
  totalAmountCurrency: string | null;
  providerSplit: Record<string, number>;
}

export function computePaymentKpis(payments: AdminPaymentItem[]): PaymentKpis {
  const succeeded = payments.filter((p) => p.status === 'succeeded').length;
  const pending = payments.filter(
    (p) => p.status === 'pending' || p.status === 'processing',
  ).length;
  const failed = payments.filter((p) => p.status === 'failed').length;
  const refunded = payments.filter(
    (p) => p.status === 'refunded' || p.status === 'partially_refunded',
  ).length;

  const succeededCharges = payments.filter(
    (p) => p.status === 'succeeded' && (p.kind === 'subscription' || p.kind === null),
  );

  const currencies = new Set(succeededCharges.map((p) => p.currency));
  const singleCurrency = currencies.size === 1 ? [...currencies][0]! : null;
  const totalAmountMinor =
    singleCurrency !== null ? succeededCharges.reduce((sum, p) => sum + p.amount, 0) : null;

  const providerSplit: Record<string, number> = {};
  for (const payment of payments) {
    const key = payment.provider_label || payment.provider;
    providerSplit[key] = (providerSplit[key] ?? 0) + 1;
  }

  return {
    total: payments.length,
    succeeded,
    pending,
    failed,
    refunded,
    totalAmountMinor,
    totalAmountCurrency: singleCurrency,
    providerSplit,
  };
}

export function formatDate(value: string | null | undefined, locale: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateTime(value: string | null | undefined, locale: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatShortId(value: string | null | undefined): string | null {
  if (!value) return null;
  if (value.length <= 12) return value;
  return `${value.slice(0, 8)}…${value.slice(-4)}`;
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function formatProviderReference(payment: AdminPaymentItem): string | null {
  return payment.provider_payment_id ?? payment.provider_invoice_id ?? payment.payment_intent_id;
}
